/**
 * Knowledge › Blocks "Recent customer questions": why a real customer question got (or did not get) a linked block,
 * read from its answer trace's link decision (tool-service `services/link_choice.py`). Pure — no imports.
 */
export interface WhyTrace {
  id: string;
  question: string;
  createdAt: string;
  textConversion?: { normalized?: string } | null;
  block?: Record<string, unknown> | null;
}

export interface WhyRow {
  id: string;
  question: string;
  /** The Cyrillic the customer's Latin was matched as (null: not converted). */
  matchedAs: string | null;
  createdAt: string;
  tone: 'sent' | 'close' | 'none' | 'test';
  /** The closest link (null: none matched) and the text to add to it as an example (the Cyrillic when converted). */
  linkId: string | null;
  exampleText: string;
  /** The closest block scored too low — adding this question as its example can help (not so when sending failed). */
  weak: boolean;
  headline: string;
  detail: string | null;
}

const RUN_DETAIL: Record<string, string> = {
  run_skipped: 'It matched, but the reply limit for this customer stopped it.',
  run_gone: 'It matched, but the block was deleted — fix or remove this link.',
  run_unconfigured: 'It matched, but sending blocks is not set up for this bot.',
  run_error: 'It matched, but sending the block failed.',
  text_failed: 'It matched, but sending failed, so the AI answered instead.',
  send_failed: 'It matched, but sending failed, so the AI answered instead.',
  no_text: 'It matched, but sending failed, so the AI answered instead.',
  no_text_channel: 'It matched, but this channel cannot send a block with a text answer.',
};

const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
const fixed = (v: number | null) => (v === null ? '' : v.toFixed(2));

export function whyRow(t: WhyTrace, titles: Record<string, string>): WhyRow | null {
  const b = t.block;
  if (!b || b.kind !== 'link') return null;
  const score = num(b.score);
  const linkId = typeof b.linkId === 'string' ? b.linkId : null;
  const ref = typeof b.blockRef === 'string' ? b.blockRef : null;
  const name = (linkId && titles[linkId]) || (ref ? `block ${ref}` : null);
  const named = name ? `${name}${score === null ? '' : ` (${fixed(score)})`}` : null;
  const th = (b.thresholds ?? {}) as { blockOnly?: unknown; attach?: unknown };
  const reason = typeof b.reason === 'string' ? b.reason : '';
  const base = {
    id: t.id,
    question: t.question,
    matchedAs: t.textConversion?.normalized || null,
    createdAt: t.createdAt,
    linkId,
    exampleText: t.textConversion?.normalized || t.question,
    weak: b.mode === 'none' && (reason === 'below_threshold' || reason === 'no_knowledge_weak'),
  };
  const row = (tone: WhyRow['tone'], headline: string, detail: string | null = null): WhyRow => ({ ...base, tone, headline, detail });

  if (b.shadow === true) {
    return b.mode === 'none' || !named ? row('test', 'Test mode — would not send a block') : row('test', `Test mode — would send: ${named}`);
  }
  if (b.mode === 'block_only' && named) return row('sent', `Block sent: ${named}`);
  if (b.mode === 'attach' && named) return row('sent', `Block sent with the AI answer: ${named}`);
  if (!named) return row('none', 'No block matched');

  const headline = `Not sent: closest block ${named}`;
  if (reason === 'below_threshold') return row('close', headline, `Needs ${fixed(num(th.attach))} to go with the answer.`);
  if (reason === 'no_knowledge_weak') {
    return row('close', headline, `No document answered and the block needs ${fixed(num(th.blockOnly))} to be sent alone, so the default block answered.`);
  }
  return row('close', headline, RUN_DETAIL[reason] ?? null);
}

/** "Show more": the next page appended, minus rows already shown — new questions arriving push older ones down a page. */
export function appendPage<T extends { id: string }>(shown: T[], next: T[]): T[] {
  const seen = new Set(shown.map((x) => x.id));
  return [...shown, ...next.filter((x) => !seen.has(x.id))];
}
